import {
    catchError,
    map,
    Observable,
    throwError
} from "rxjs";

import { HttpClient } from "@angular/common/http";
import {
    Injectable, inject
} from "@angular/core";

import { IRequestModel } from "@shopliftr/common-js/data";
import {
    FileCopyRequest,
    FileDeleteRequest,
    FileListRequest,
    FileResponse,
    FileUploadRequest
} from "@shopliftr/common-js/shared";

import { ValidationException } from "../../../../exceptions/validation-exception";
import { InvalidParametersException } from "../../../../exceptions/invalid-parameters-exception";
import { COMMON_ERROR_HANDLER } from "../../../../exceptions/constants/exception-constants";
import { INTERNAL_API_APP_CONFIG } from "../../../constants/iapi-app-tokens";

/**
 * HTTP request service for file operations against Internal API v2.
 *
 * Handles file upload, delete, copy, and list operations. This service owns
 * the file-specific HTTP communication that was previously part of
 * {@link InternalApiRequestService}.
 *
 * @pattern Request Service / File Operations
 * @providedIn root
 */
@Injectable(
    { providedIn: "root" }
)
export class InternalApiFileRequestService {

    private static readonly _DELETE_FILE_PATH = "file/delete";

    private static readonly _COPY_FILE_PATH = "file/copy";

    private static readonly _LIST_FILE_PATH = "file/list";

    private static readonly _UPLOAD_FILE_PATH = "file/upload";

    private readonly _httpClient = inject(HttpClient);
    private readonly _errorHandler = inject(COMMON_ERROR_HANDLER);
    private readonly _appConfig = inject(INTERNAL_API_APP_CONFIG);

    /**
     * Performs a File Upload Action with the provided request against the file upload end point.
     *
     * @param request - The file upload request containing metadata
     * @param file - The file blob to upload
     * @returns Observable that emits the file operation response
     */
    uploadFile$(request: FileUploadRequest<unknown>, file: Blob): Observable<FileResponse> {

        try {
            this._preprocessRequest(request);
            if (!file) {
                throw new InvalidParametersException("file is required");
            }

            const body = new FormData();
            body.append("file", file);
            body.append("requestId", request.requestId);
            body.append("subjectName", request.subjectName);
            body.append("action", request.action);
            body.append("filePath", request.filePath);
            body.append("contentType", request.contentType);

            if (request.optionsModelName && request.options) {
                body.append("optionsModelName", request.optionsModelName);
                body.append("options", JSON.stringify(request.options));
            }

            return this._httpClient.put(
                this._getUrl(request, InternalApiFileRequestService._UPLOAD_FILE_PATH),
                body
            ).pipe(
                map((result: unknown) => {

                    return FileResponse.deserialize(result);
                }),
                catchError((error: unknown) => {

                    return throwError(() => this._errorHandler.handleServiceError(error));
                })
            );
        }
        catch (error) {

            return throwError(() => this._errorHandler.handleServiceError(error));
        }
    }

    /**
     * Performs a File Delete Action with the provided request against the file delete end point.
     *
     * @param request - The file delete request containing file paths to delete
     * @returns Observable that emits the file operation response
     */
    deleteFiles$(request: FileDeleteRequest<unknown>): Observable<FileResponse> {

        try {
            this._preprocessRequest(request);
            return this._httpClient.post(
                this._getUrl(request, InternalApiFileRequestService._DELETE_FILE_PATH),
                request.serialize()
            ).pipe(
                map((result: unknown) => {

                    return FileResponse.deserialize(result);
                }),
                catchError((error: unknown) => {

                    return throwError(() => this._errorHandler.handleServiceError(error));
                })
            );
        }
        catch (error) {

            return throwError(() => this._errorHandler.handleServiceError(error));
        }
    }

    /**
     * Performs a File Copy Action with the provided request against the file copy end point.
     *
     * @param request - The file copy request containing source and destination paths
     * @returns Observable that emits the file operation response
     */
    copyFiles$(request: FileCopyRequest<unknown>): Observable<FileResponse> {

        try {
            this._preprocessRequest(request);
            return this._httpClient.post(
                this._getUrl(request, InternalApiFileRequestService._COPY_FILE_PATH),
                request.serialize()
            ).pipe(
                map((result: unknown) => {

                    return FileResponse.deserialize(result);
                }),
                catchError((error: unknown) => {

                    return throwError(() => this._errorHandler.handleServiceError(error));
                })
            );
        }
        catch (error) {

            return throwError(() => this._errorHandler.handleServiceError(error));
        }
    }

    /**
     * Performs a File List Action with the provided request against the file list end point.
     *
     * @param request - The file list request containing filter criteria
     * @returns Observable that emits the file operation response
     */
    listFiles$(request: FileListRequest<unknown>): Observable<FileResponse> {

        try {
            this._preprocessRequest(request);
            return this._httpClient.post(
                this._getUrl(request, InternalApiFileRequestService._LIST_FILE_PATH),
                request.serialize()
            ).pipe(
                map((result: unknown) => {

                    return FileResponse.deserialize(result);
                }),
                catchError((error: unknown) => {

                    return throwError(() => this._errorHandler.handleServiceError(error));
                })
            );
        }
        catch (error) {

            return throwError(() => this._errorHandler.handleServiceError(error));
        }
    }

    /**
     * Validates and pre-processes the given request against the given action.
     */
    private _preprocessRequest(request: IRequestModel<unknown, unknown>): void {

        if (!request?.action) {
            throw new ValidationException(`No request was provided`);
        }

        request.onAction(request.action);

        const validationResult = request.validate(request.action);

        if (!validationResult.valid) {
            throw new ValidationException(`Request was invalid: ${validationResult.errors.join(", ")}`);
        }
    }

    /**
     * Returns the url for the request.
     */
    private _getUrl(request: IRequestModel<unknown, unknown>, basePath: string): string {

        let path = `/v2/${basePath}`;
        if (request.urlPath) {
            path = path + request.urlPath;
        }
        const url = new URL(path, this._appConfig.apiUrl);

        return url.toString();
    }
}
